import React from 'react'
import {Box, Text} from '@chakra-ui/react'
import PropTypes from 'prop-types'

/**
 * GDIRangeMarker
 *
 * Renders a horizontal range bar with evenly-spaced labelled tick marks and a
 * filled-circle position indicator.
 *
 * Positions are expressed on a 0→(N-1) numeric scale, where N is the number
 * of labels. Decimal values are supported — for example with 3 labels:
 *   0   → first label (left)
 *   1   → second label (middle)
 *   0.7 → 35 % across the track (between first and second)
 *   2   → third label (right)
 *
 * Props:
 *   labels  — array of label strings; at least 2 required for a visible range
 *   value   — numeric marker position (0 ≤ value ≤ labels.length - 1)
 *   color   — optional CSS/token override for the track, ticks, and marker
 *             (defaults to currentColor so it inherits from the parent text)
 *   mt / mb / my / mx / m — forwarded as margin shorthands
 */
const GDIRangeMarker = ({labels = [], value = 0, color, ...props}) => {
    // Need at least 2 labels to form a range; fall back gracefully with 1.
    const count = labels.length
    if (count === 0) return null

    const max = Math.max(count - 1, 1)
    // Clamp to [0, max] and convert to percentage
    const clampedValue = Math.min(Math.max(Number(value) || 0, 0), max)
    const markerPct = (clampedValue / max) * 100

    const trackColor = color ? `var(--chakra-colors-${String(color).replace(/\./g, '-')})` : 'currentColor'

    const tickH = 8       // px — full height of vertical tick mark
    const halfTick = tickH / 2  // tick extends this far above AND below the track
    const markerR = 5     // px — radius of the marker circle (≥ halfTick so pt = markerR covers both)
    const labelGap = 6    // px — gap between tick bottom and label text

    // pt = markerR  → circle isn't clipped above
    // pb = halfTick + labelGap + 14  → tick-below + gap + approx label line-height
    // (all children are absolute, so box height = pt + pb)
    return (
        <Box
            position="relative"
            width="100%"
            pt={`${markerR}px`}
            pb={`${halfTick + labelGap + 14}px`}
            {...props}
        >
            {/* ── Track line ── */}
            <Box
                position="absolute"
                top={`${markerR}px`}
                left={0}
                right={0}
                height="1px"
                backgroundColor={trackColor}
                aria-hidden="true"
            />

            {/* ── Tick marks + labels ── */}
            {labels.map((label, i) => {
                const leftPct = count > 1 ? (i / max) * 100 : 50

                // Edge labels must not overflow: first aligns left, last aligns right.
                const isFirst = i === 0
                const isLast = i === count - 1
                const transform = isFirst
                    ? 'translateX(0)'
                    : isLast
                    ? 'translateX(-100%)'
                    : 'translateX(-50%)'
                const alignItems = isFirst ? 'flex-start' : isLast ? 'flex-end' : 'center'

                // When the marker circle sits exactly on this tick, hide the tick
                // line — the filled circle already marks the position.
                const isMarked = clampedValue === i

                return (
                    <Box
                        key={i}
                        position="absolute"
                        // Position container so the tick spans halfTick above AND below the track.
                        top={`${markerR - halfTick}px`}
                        left={`${leftPct}%`}
                        transform={transform}
                        display="flex"
                        flexDirection="column"
                        alignItems={alignItems}
                        aria-hidden="true"
                    >
                        {/* Tick line — omitted when the marker circle is at this position */}
                        {!isMarked && (
                            <Box
                                width="1px"
                                height={`${tickH}px`}
                                backgroundColor={trackColor}
                            />
                        )}
                        {/* Label — when tick is hidden add its height to mt so
                            all labels sit on the same baseline */}
                        <Text
                            as="span"
                            textStyle="bodyXsRegular"
                            color={color ? trackColor : 'inherit'}
                            lineHeight={1}
                            mt={`${isMarked ? tickH + labelGap : labelGap}px`}
                            whiteSpace="nowrap"
                            userSelect="none"
                        >
                            {label}
                        </Text>
                    </Box>
                )
            })}

            {/* ── Position marker (filled circle) ── */}
            <Box
                position="absolute"
                top={0}
                left={`${markerPct}%`}
                transform="translateX(-50%)"
                width={`${markerR * 2}px`}
                height={`${markerR * 2}px`}
                borderRadius="full"
                backgroundColor={trackColor}
                aria-label={`Position: ${value}`}
            />
        </Box>
    )
}

GDIRangeMarker.propTypes = {
    labels: PropTypes.arrayOf(PropTypes.string),
    value: PropTypes.number,
    color: PropTypes.string
}

export default GDIRangeMarker
